import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { LookupDialogDimensions } from '@core/interfaces';
import { EventService } from '@core/services';
import { dialogAttributes } from '@util/dialog';

import { endpoints } from '@environment';

import {
  ConsigneeCustomerLookupRequest,
  ConsigneeCustomerLookupResponseBody,
  ConsigneeLookupRequest,
  ConsigneeLookupResponseBody,
} from '#types/consignee';

import { ConsigneeLookupDialogComponent } from '@consignee/consignee-lookup-dialog/consignee-lookup-dialog.component';
import { CONSTANTS } from '@consignee/constants';
import { CustomerConsigneeLookupDialogComponent } from '@consignee/customer-consignee-lookup-dialog/customer-consignee-lookup-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class ConsigneeService {
  consigneeLookupRequestLoading$ = new Subject<boolean>();
  customerConsigneeLookupRequestLoading$ = new Subject<boolean>();

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  consigneeLookupDialogDimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  customerConsigneeLookupDialogDimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  consigneeLookupDialogBillTo$ = new BehaviorSubject<string>(null);
  consigneeLookupDialogShipTo$ = new BehaviorSubject<string>(null);
  consigneeLookupDialogHideBillToShipToFilter$ = new BehaviorSubject<boolean>(false);

  customerConsigneeLookupDialogCustId$ = new BehaviorSubject<string>(null);
  customerConsigneeLookupDialogBillTo$ = new BehaviorSubject<string>(null);
  customerConsigneeLookupDialogShipTo$ = new BehaviorSubject<string>(null);
  customerConsigneeLookupDialogHideBillToShipToFilter$ = new BehaviorSubject<boolean>(false);

  consigneeLookupDialoggridId = CONSTANTS.GRID_CONSIGNEE_LOOKUP_DIALOG;
  customerConsigneeLookupDialoggridId = CONSTANTS.GRID_CUSTOMER_CONSIGNEE_LOOKUP_DIALOG;

  constructor(
    private http: HttpClient,
    private eventService: EventService,
    private translocoService: TranslocoService,
    private dialogService: DialogService
  ) {}

  getConsigneeLookup(payload: ConsigneeLookupRequest) {
    return this.http.post(endpoints.consignee.consignee, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_CONSIGNEE_LOOKUP_DIALOG),
      map((response: ConsigneeLookupResponseBody) => response)
    );
  }

  getCustomerConsigneeLookup(payload: ConsigneeCustomerLookupRequest) {
    return this.http.post(endpoints.consignee.customerConsignee, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_CUSTOMER_CONSIGNEE_LOOKUP_DIALOG),
      map((response: ConsigneeCustomerLookupResponseBody) => response)
    );
  }

  consigneeLookupDialog(
    options: { billTo?: string; shipTo?: string; hideBillToShipToFilter?: boolean } = {
      billTo: 'Y',
      shipTo: 'Y',
      hideBillToShipToFilter: false,
    },
    config: DynamicDialogConfig = {}
  ): DynamicDialogRef {
    this.consigneeLookupDialogBillTo$.next(options.billTo ?? 'Y');
    this.consigneeLookupDialogShipTo$.next(options.shipTo ?? 'Y');
    this.consigneeLookupDialogHideBillToShipToFilter$.next(options.hideBillToShipToFilter ?? false);

    return this.dialogService.open(ConsigneeLookupDialogComponent, {
      ...dialogAttributes,
      header: this.translate('consignee.consignee_lookup_dialog.dialog.title'),
      styleClass: 'lookup-dialog large-dialog',
      ...config,
    });
  }

  customerConsigneeLookupDialog(
    custid: string,
    options: { billTo?: string; shipTo?: string; hideBillToShipToFilter?: boolean } = {
      billTo: 'Y',
      shipTo: 'Y',
      hideBillToShipToFilter: false,
    },
    config: DynamicDialogConfig = {}
  ): DynamicDialogRef {
    this.customerConsigneeLookupDialogCustId$.next(custid);
    this.customerConsigneeLookupDialogBillTo$.next(options.billTo ?? 'Y');
    this.customerConsigneeLookupDialogShipTo$.next(options.shipTo ?? 'Y');
    this.customerConsigneeLookupDialogHideBillToShipToFilter$.next(options.hideBillToShipToFilter ?? false);

    return this.dialogService.open(CustomerConsigneeLookupDialogComponent, {
      ...dialogAttributes,
      header: this.translate('consignee.consignee_lookup_dialog.dialog.title'),
      styleClass: 'lookup-dialog large-dialog',
      ...config,
    });
  }
}
